'use client';

import { useState } from 'react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';

// ── Data ─────────────────────────────────────────────────────────────────────

const PROJECTS = [
  {
    id: 'sourcing',
    number: 'A',
    title: 'Sole-Source AWP Parts — Sourcing & Risk Deep-Dive',
    objective:
      'Identify your highest-risk OEM sole-source parts holds, research alternate sourcing paths, and draft a 30/60/90-day action memo.',
    files: ['Depot_Supply_AWP_NOTIONAL.csv', 'Depot_WIP_NOTIONAL.csv'],
    prompt: `You are a depot supply-chain analyst supporting a Marine Corps aviation depot readiness review. All data in this exercise is NOTIONAL — for training purposes only. Take your time and use deep research / web browsing to back your findings with citations where possible.

────────────────────────────
YOUR TASK
────────────────────────────

Using the attached Depot_Supply_AWP_NOTIONAL.csv and Depot_WIP_NOTIONAL.csv, complete the following research mission.

STEP 1 — Isolate the highest-risk parts holds.
From the supply file, extract every row where:
  • Source = "OEM-Sole Source"
  • Supply_Status is "Backordered" or "Partial Receipt"
  • Lead_Time_Days ≥ 90

List these parts with their NSN_NIIN, Part_Nomenclature, Platform, linked WO_Number, and Lead_Time_Days.

STEP 2 — Cross-reference blocked work orders.
In the WIP file, find every job where AWP_Flag = Y that matches the work orders from Step 1. Note each job's Priority, Percent_Complete, Days_In_WIP, and Customer.

STEP 3 — Research the parts.
For each part from Step 1, use web search to investigate:
  • Which OEM or prime contractor is the likely sole source for this category of aviation component (general aerospace/military MRO knowledge — not real acquisition data)?
  • Are there known DLA-approved alternates, Qualified Products Lists (QPL), or NSN cross-references for this part category?
  • What are realistic commercial lead-time ranges for similar aviation MRO parts sourced through CAGE or DLA supply chains?
  • Cite any relevant DAU, DLA, NAVAIR, or MRO industry sources you find.

STEP 4 — Build a sourcing strategy table.
Present a table with columns:
  Part | Platform | Current Lead Time | Recommended Primary Action | Recommended Alternate Action | Risk Level (High / Med / Low)

STEP 5 — Draft a commander's action memo.
Write a one-page (5-paragraph BLUF-format) memo from the depot commander to the supply officer, summarizing the AWP risk, the sourcing findings, and a 30/60/90-day action plan to reduce sole-source dependency.

────────────────────────────
Before you begin, ask me any clarifying questions that would sharpen your analysis.`,
  },
  {
    id: 'throughput',
    number: 'B',
    title: 'Throughput Drift Root-Cause & MRO Benchmark',
    objective:
      'Quantify schedule-adherence drift by product line, benchmark against industry MRO standards, and deliver a ranked root-cause list with a next-quarter projection.',
    files: [
      'Depot_Schedule_Throughput_NOTIONAL.csv',
      'Depot_WIP_NOTIONAL.csv',
    ],
    prompt: `You are an industrial operations analyst specializing in military MRO (maintenance, repair & overhaul). All data is NOTIONAL — training use only. Use deep research / web browsing to bring in external benchmarks and citations.

────────────────────────────
YOUR TASK
────────────────────────────

Using the attached Depot_Schedule_Throughput_NOTIONAL.csv and Depot_WIP_NOTIONAL.csv, run a full throughput health analysis.

STEP 1 — Trend Schedule_Adherence_Pct by product line.
For each of the five product lines (AV-8B Components, CH-53E Dynamic Components, CH-53K Power Plants, MV-22B Structures, H-1 Hydraulics), compute the 6-month trend in Schedule_Adherence_Pct (January–June 2026) and the monthly delta between Actual_TAT_Days and Target_TAT_Days. Identify the single worst-drifting product line using quantitative criteria. Show your work.

STEP 2 — Cross-reference WIP floor data for the worst line.
In the WIP file, examine jobs for the platform driving that product line. Look at:
  • Status distribution (count of AWP, AWM, Evaluation, Disassembly, In Repair)
  • Days_In_Current_Status for stalled jobs (flag anything > 30 days in a non-final status)
  • Which Lead_Artisan teams have the most Past_ECD = Y jobs

STEP 3 — Research MRO benchmarks.
Using web search, find:
  • Typical depot-level MRO turnaround time benchmarks for similar aviation component overhaul work (rotary/fixed-wing, structures vs. dynamic components vs. power plants)
  • Industry or DoD sources (NAVAIR, AFSC, DAU, Aviation Week MRO, Oliver Wyman Fleet Forecast, GAO readiness reports, etc.) that define schedule adherence targets for military MRO
  • Any published root-cause frameworks for TAT drift in depot MRO environments

Cite all sources with URLs or document titles.

STEP 4 — Deliver a ranked root-cause hypothesis list.
Based on the data and your research, provide a ranked list (1–5) of the most likely root causes for the drift you identified. For each: state your confidence level (High / Med / Low) and the specific data evidence supporting it.

STEP 5 — Next-quarter projection + improvement playbook.
Project next-quarter output under two scenarios:
  (A) Do nothing — extrapolate current trend
  (B) Implement your top 2 improvement levers

For each lever, cite at least one real-world example or published case study of a depot or MRO operation that applied it successfully.

────────────────────────────
Before you begin, ask me any clarifying questions that would help you frame the analysis correctly.`,
  },
  {
    id: 'forecast',
    number: 'C',
    title: '90-Day Readiness Risk Forecast',
    objective:
      'Synthesize all three data files into a likelihood-by-impact risk register, recommended job sequencing, and a flag-level commander\'s brief.',
    files: [
      'Depot_WIP_NOTIONAL.csv',
      'Depot_Supply_AWP_NOTIONAL.csv',
      'Depot_Schedule_Throughput_NOTIONAL.csv',
    ],
    prompt: `You are a readiness analyst supporting a senior depot commander's 90-day look-ahead brief. All data is NOTIONAL — training use only. Use deep research and web browsing to enrich the operational context.

────────────────────────────
YOUR TASK
────────────────────────────

Using all three attached files (Depot_WIP_NOTIONAL.csv, Depot_Supply_AWP_NOTIONAL.csv, Depot_Schedule_Throughput_NOTIONAL.csv), build a comprehensive 90-day readiness risk forecast.

STEP 1 — Identify every at-risk work order.
From the WIP file, extract all jobs where Past_ECD = Y AND Status ≠ RFI. For each, note: Platform, End_Item, Customer, Priority, Percent_Complete, and Days_In_WIP.

STEP 2 — Layer in supply risk.
Cross-reference those jobs against the supply file using WO_Number. For each at-risk job determine:
  • Is there a linked parts hold?
  • What is the Supply_Status and EDD?
  • Is the source OEM-Sole Source?

STEP 3 — Research platform operational significance.
Using web search, briefly investigate the operational role and readiness sensitivity of each platform appearing in your at-risk list (CH-53E, CH-53K, F-35B, MV-22B, AV-8B, AH-1Z, UH-1Y, KC-130J). For each platform, find:
  • Primary mission sets it supports
  • Any current readiness challenges noted in public DoD, GAO, or Congressional reporting
  • General DoD/GAO cost-of-downtime framing (e.g., cost per flying hour, mission-capable rate targets)

Cite all sources.

STEP 4 — Build a risk register.
Create a risk register table with columns:
  WO | Platform | End Item | Priority | Days Past ECD | Parts Blocked (Y/N) | Likelihood of Missing 90-Day Window (H/M/L) | Mission Impact (H/M/L) | Recommended Action

STEP 5 — Recommend sequencing for the next 10 recovery jobs.
Given the full risk picture, identify which 10 jobs the depot should prioritize for recovery action in the next 30 days. Provide your rationale for each selection (supply status, mission impact, proximity to completion, platform criticality).

STEP 6 — Draft a commander's 90-day brief.
Write a 6-bullet BLUF commander's brief suitable for a flag-level readiness review, covering:
  1. Current floor status
  2. Top 3 schedule risks
  3. Supply chain vulnerability summary
  4. Recommended actions
  5. Resources or decisions required from higher headquarters
  6. Outlook — projected status if top actions are taken

────────────────────────────
Before you begin, ask me any clarifying questions that would improve the accuracy of your forecast.`,
  },
];

// ── Icons ────────────────────────────────────────────────────────────────────

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    >
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="5" y="5" width="9" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8l4 4 6-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Project card ─────────────────────────────────────────────────────────────

function ProjectCard({ project }: { project: (typeof PROJECTS)[number] }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(project.prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-xl border border-violet-100 bg-white overflow-hidden transition-all">
        {/* Header row — always visible */}
        <CollapsibleTrigger className="w-full text-left">
          <div className="flex items-start gap-3 px-4 py-3 hover:bg-violet-50/40 transition-colors cursor-pointer">
            {/* Letter badge */}
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 bg-violet-600 text-white mt-0.5">
              {project.number}
            </span>

            {/* Text block */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 leading-snug">
                {project.title}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                {project.objective}
              </p>
              {/* File pills */}
              <div className="flex flex-wrap gap-1 mt-1.5">
                {project.files.map((f) => (
                  <span
                    key={f}
                    className="text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>

            {/* Chevron */}
            <span className="text-violet-400 mt-1">
              <ChevronDown open={open} />
            </span>
          </div>
        </CollapsibleTrigger>

        {/* Expanded: prompt + copy button */}
        <CollapsibleContent>
          <div className="border-t border-violet-100 px-4 pb-4 pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet-500">
                Paste this prompt into ChatGPT or Claude
              </p>
              <button
                onClick={handleCopy}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-all ${
                  copied
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100'
                }`}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
                {copied ? 'Copied!' : 'Copy prompt'}
              </button>
            </div>
            <pre className="whitespace-pre-wrap text-[11px] leading-relaxed font-mono text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-3 overflow-x-auto">
              {project.prompt}
            </pre>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function DeepResearchProjects() {
  return (
    <div className="rounded-2xl border border-violet-200 bg-white overflow-hidden">
      {/* Section header */}
      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-violet-100 bg-violet-50/60">
        <div className="w-6 h-6 rounded-md bg-violet-100 flex items-center justify-center shrink-0">
          <svg
            width="13"
            height="13"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="8" cy="8" r="5" stroke="#7c3aed" strokeWidth="1.6" />
            <path d="M12.5 12.5L17 17" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round" />
            <path d="M8 5.5v5M5.5 8h5" stroke="#7c3aed" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-violet-700">
            Deep Research Projects — ChatGPT / Claude
          </p>
        </div>
      </div>

      {/* Intro */}
      <div className="px-4 pt-3 pb-2">
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Download the three training spreadsheets above, attach them to{' '}
          <strong className="text-slate-700">ChatGPT</strong> or{' '}
          <strong className="text-slate-700">Claude</strong>, enable{' '}
          <span className="font-semibold text-violet-700">Deep Research</span> or web browsing,
          then paste a prompt below and let it run. Each project synthesizes the data
          with open-source research to produce actionable depot analysis.
        </p>
      </div>

      {/* Project cards */}
      <div className="px-3 pb-3 space-y-1.5">
        {PROJECTS.map((p) => (
          <ProjectCard key={p.id} project={p} />
        ))}
      </div>
    </div>
  );
}
